import { useState, useMemo, useEffect } from 'react';
import DateRangePicker from './DateRangePicker';
import { toM, fmt$, fmt$0 } from '../utils/formatters';
import { getCategories, getAllVisibleCategories } from '../utils/categories';
import { supabase } from '../lib/supabase';
import { getRange, fmtDateShort, PRESETS } from '../utils/dateRange';
import { parseLocalDate } from '../utils/dates';
import { getInventoryCountsForRange } from '../lib/loadData';
import { getInventoryCOGS } from '../utils/inventoryCOGS';

function calcDeductions(customDeductions,totalSales,salesByCategory,daysInRange){
let total=0;
for(const d of(customDeductions||[])){
if(d.type==='pct_total_sales'){total+=totalSales*(d.value/100);}
else if(d.type==='pct_category_sales'){const catSales=salesByCategory?.[d.category]||0;total+=catSales*(d.value/100);}
else if(d.type==='flat_monthly'){total+=d.value*(daysInRange/30.44);}
}
return total;
}

export default function MonthlyPL({t,si,mn,salesByDate,wholesaleByDate,laborByDate,wasteLog,purchases,otherExp,fx,ings,settings,calcWCost,avgCP,fxTot,orgId,uid,customDeductions}){
const[preset,setPreset]=useState('this-month');
const[customStart,setCustomStart]=useState('');const[customEnd,setCustomEnd]=useState('');
const range=getRange(preset,{start:customStart,end:customEnd});
const[invCountRows,setInvCountRows]=useState([]);
useEffect(()=>{if(orgId)getInventoryCountsForRange(supabase,orgId,range.end).then(setInvCountRows);},[orgId,range.end]);

const data=useMemo(()=>{
  const inRange=d=>d>=range.start&&d<=range.end;
  const nv=d=>!laborByDate[d]?.voided;
  const ok=d=>inRange(d)&&nv(d);
  // Count days in range
  const startD=parseLocalDate(range.start),endD=parseLocalDate(range.end);
  const daysInRange=Math.round((endD-startD)/(1000*60*60*24))+1;
  const monthsInRange=daysInRange/30.44; // for fixed cost proration

  // Revenue by POS category (exclude voided days)
  let totalSales=0;const catSales={};
  getAllVisibleCategories(settings,salesByDate,ok).forEach(c=>catSales[c]=0);
  Object.entries(salesByDate).filter(([d])=>ok(d)).forEach(([,row])=>{
    Object.entries(row).forEach(([cat,amt])=>{
      const v=parseFloat(String(amt).replace(/,/g,''))||0;
      totalSales+=v;
      if(catSales[cat]!==undefined)catSales[cat]+=v;
    });
  });
  let wholesaleTotal=0;
  Object.entries(wholesaleByDate||{}).filter(([d])=>ok(d)).forEach(([,v])=>{wholesaleTotal+=Number(v||0);});
  totalSales+=wholesaleTotal;

  // COGS by purchase category (exclude voided days)
  const cogsByCat={Food:0,Beverages:0,'Paper Goods':0,'Retail Goods':0};
  purchases.filter(p=>ok(p.date)).forEach(p=>{
    const cat=p.category||'Food';
    cogsByCat[cat]=(cogsByCat[cat]||0)+p.amt;
  });
  const totalPurchases=Object.values(cogsByCat).reduce((s,v)=>s+v,0);
  // Theoretical COGS = POS sales × avgCP (excludes wholesale)
  const posSales=totalSales-wholesaleTotal;
  const theoreticalCOGS=posSales*avgCP;
  const todayStr=new Date().toISOString().split('T')[0];
  const isOpenPeriod=range.end>=todayStr;
  const invCOGS=isOpenPeriod?null:getInventoryCOGS(invCountRows,ings||[],range.start,range.end,totalPurchases);
  const totalCOGS=isOpenPeriod?theoreticalCOGS:(invCOGS?.cogs??totalPurchases);
  const grossProfit=totalSales-totalCOGS;

  // Labor (exclude voided days)
  let totalLabor=0,totalCash=0;
  Object.entries(laborByDate).filter(([d])=>ok(d)).forEach(([,v])=>{
    totalLabor+=parseFloat(String(v.labor||'').replace(/,/g,''))||0;
    totalCash+=parseFloat(String(v.cash||'').replace(/,/g,''))||0;
  });

  // Waste (exclude voided days)
  const totalWaste=wasteLog.filter(w=>ok(w.date)).reduce((s,w)=>s+calcWCost(w),0);

  // Fixed costs prorated to range — split by category
  const uncategorizedFxTot=fx.filter(e=>!e.category).reduce((s,e)=>s+toM(e.amt,e.per),0);
  const monthlyFixed=uncategorizedFxTot*monthsInRange;
  // Categorized fixed costs roll into catExpByCategory below
  const categorizedFx={};
  fx.filter(e=>e.category).forEach(e=>{const mo=toM(e.amt,e.per)*monthsInRange;categorizedFx[e.category]=(categorizedFx[e.category]||0)+mo;});

  // Percentage-based costs
  const dedTotal=calcDeductions(customDeductions,totalSales,catSales,daysInRange);
  const cardSales=Math.max(0,totalSales-totalCash);
  const ccFees=cardSales*(settings.ccPct||0)/100;

  // Categorized expenses by category (from otherExp prop, filtered by range)
  const catExpByCategory={};
  otherExp.filter(e=>ok(e.date)).forEach(e=>{
    catExpByCategory[e.category]=(catExpByCategory[e.category]||0)+e.amt;
  });
  // Merge categorized fixed costs into expense categories
  for(const[cat,amt] of Object.entries(categorizedFx)){catExpByCategory[cat]=(catExpByCategory[cat]||0)+amt;}
  const totalCatExp=Object.values(catExpByCategory).reduce((s,v)=>s+v,0);

  const allFixedProrated=fxTot*monthsInRange;
  const totalOpEx=totalLabor+totalWaste+dedTotal+ccFees+monthlyFixed+totalCatExp;
  const netIncome=grossProfit-totalOpEx;

  return{totalSales,wholesaleTotal,catSales,cogsByCat,totalCOGS,totalPurchases,theoreticalCOGS,isOpenPeriod,invCOGS,grossProfit,totalLabor,totalWaste,dedTotal,ccFees,monthlyFixed,allFixedProrated,catExpByCategory,totalCatExp,totalOpEx,netIncome,cardSales,daysInRange};
},[range.start,range.end,salesByDate,wholesaleByDate,laborByDate,wasteLog,purchases,otherExp,fx,settings,avgCP,fxTot,invCountRows,ings,customDeductions]);

const d=data;
const line=(label,val,color,bold,indent)=>(
<div key={label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:bold?"10px 0":"6px 0",borderBottom:`1px solid ${t.bd}`,paddingLeft:indent?20:0}}>
<span style={{fontSize:bold?14:13,fontWeight:bold?700:400,color:bold?t.tx:t.tm}}>{label}</span>
<span style={{fontFamily:mn,fontSize:bold?16:14,fontWeight:bold?700:500,color}}>{val>=0?"$":"-$"}{fmt$(Math.abs(val))}</span>
</div>);

const pct=(v)=>d.totalSales>0?(v/d.totalSales*100).toFixed(1)+"%":"—";

return(<div>
<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
<h1 style={{fontSize:22,fontWeight:700,margin:0}}>Monthly P&L</h1>
</div>

{/* Date range selector */}
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:"14px 18px",marginBottom:16}}>
<div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
{PRESETS.map(p=><button key={p.id} onClick={()=>setPreset(p.id)} style={{border:`1px solid ${preset===p.id?t.bl:t.bd}`,borderRadius:8,padding:"6px 12px",fontSize:10,fontWeight:600,cursor:"pointer",color:preset===p.id?"#fff":t.tm,background:preset===p.id?t.bl:t.sf,fontFamily:"'DM Sans',sans-serif"}}>{p.label}</button>)}
</div>
{preset==='custom'&&<div style={{marginTop:10}}>
<DateRangePicker start={customStart} end={customEnd} onChange={(s,e)=>{setCustomStart(s);setCustomEnd(e);}} t={t} si={si}/>
</div>}
<div style={{fontSize:11,color:t.tm,marginTop:8}}>{fmtDateShort(range.start)} — {fmtDateShort(range.end)}</div>
</div>

{/* Summary cards */}
<div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
{[["Revenue",d.totalSales,t.gn],["Gross Profit",d.grossProfit,d.grossProfit>=0?t.gn:t.ac],["Operating Exp",d.totalOpEx,t.ac],["Net Income",d.netIncome,d.netIncome>=0?t.gn:t.ac]].map(([l,v,c])=>
<div key={l} style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:"16px 18px"}}>
<div style={{fontSize:10,color:t.tm,textTransform:"uppercase",marginBottom:6}}>{l}</div>
<div style={{fontSize:22,fontWeight:700,fontFamily:mn,color:c}}>{v>=0?"$":"-$"}{fmt$0(Math.abs(v))}</div>
<div style={{fontSize:10,color:t.td,marginTop:2}}>{pct(Math.abs(v))} of sales</div>
</div>)}
</div>

{/* Full P&L */}
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:"24px 28px"}}>
<div style={{fontSize:11,color:t.tm,textTransform:"uppercase",letterSpacing:2,marginBottom:16}}>Profit & Loss — {range.label}</div>

{/* Revenue */}
<div style={{fontSize:10,color:t.tm,textTransform:"uppercase",letterSpacing:1,marginBottom:6,marginTop:12}}>Revenue</div>
{Object.keys(d.catSales).filter(c=>d.catSales[c]>0).map(c=>line(c,d.catSales[c],t.gn,false,true))}
{d.wholesaleTotal>0&&line("Wholesale Revenue",d.wholesaleTotal,t.gn,false,true)}
{line("TOTAL REVENUE",d.totalSales,t.gn,true)}

{/* COGS by purchase category */}
<div style={{fontSize:10,color:t.tm,textTransform:"uppercase",letterSpacing:1,marginBottom:6,marginTop:16}}>Cost of Goods Sold{d.isOpenPeriod&&<span style={{fontSize:9,color:t.yl,fontStyle:'italic',textTransform:'none',letterSpacing:0,marginLeft:8}}>estimated — period in progress</span>}</div>
{d.isOpenPeriod?<>
{line("Theoretical COGS (sales × recipe cost%)",d.theoreticalCOGS,t.yl,false,true)}
<div style={{fontSize:9,color:t.td,paddingLeft:20,paddingBottom:4}}>Actual purchases so far: ${fmt$(d.totalPurchases)}</div>
</>:<>
<div style={{fontSize:9,color:t.td,textTransform:'uppercase',letterSpacing:0.5,paddingLeft:20,paddingTop:4,paddingBottom:2}}>Purchases</div>
{Object.entries(d.cogsByCat).filter(([,v])=>v>0).sort((a,b)=>b[1]-a[1]).map(([c,v])=>line(c,v,t.yl,false,true))}
{d.totalPurchases===0&&line("No purchases logged this period",0,t.td,false,true)}
{line("Total Purchases",d.totalPurchases,t.yl,false,false)}
{d.invCOGS?<>
{line(`+ Beginning Inventory (${fmtDateShort(d.invCOGS.beginDate)})`,d.invCOGS.beginValue,t.tm,false,true)}
{line(`− Ending Inventory (${fmtDateShort(d.invCOGS.endDate)})`,d.invCOGS.endValue,t.tm,false,true)}
</>:null}
</>}
{line("TOTAL COGS",d.totalCOGS,t.yl,true)}

{line("GROSS PROFIT",d.grossProfit,d.grossProfit>=0?t.gn:t.ac,true)}

{/* Operating Expenses */}
<div style={{fontSize:10,color:t.tm,textTransform:"uppercase",letterSpacing:1,marginBottom:6,marginTop:16}}>Operating Expenses</div>
{line("Labor",d.totalLabor,t.tm,false,true)}
{(customDeductions||[]).map(cd=><span key={cd.id}>{line(`${cd.name} (${cd.value}${cd.type==='flat_monthly'?'/mo':'%'})`,cd.type==='pct_total_sales'?d.totalSales*(cd.value/100):cd.type==='pct_category_sales'?(d.catSales[cd.category]||0)*(cd.value/100):cd.value*(d.daysInRange/30.44),t.bl,false,true)}</span>)}
{line(`CC Processing (${settings.ccPct}%)`,d.ccFees,t.tm,false,true)}
{line("Waste",d.totalWaste,t.ac,false,true)}
{d.monthlyFixed>0&&line(`Fixed Costs (${d.daysInRange} days)`,d.monthlyFixed,t.td,false,true)}

{Object.keys(d.catExpByCategory).length>0&&<>
<div style={{fontSize:9,color:t.td,textTransform:"uppercase",letterSpacing:1,marginTop:10,marginBottom:4,paddingLeft:20}}>Categorized Expenses</div>
{Object.entries(d.catExpByCategory).sort((a,b)=>b[1]-a[1]).map(([cat,amt])=>line(cat,amt,t.td,false,true))}
</>}

{line("TOTAL OPERATING EXPENSES",d.totalOpEx,t.ac,true)}
{d.allFixedProrated>0&&<div style={{fontSize:10,color:t.td,fontStyle:'italic',paddingLeft:20,marginTop:2}}>Includes ${fmt$(d.allFixedProrated)} in fixed monthly costs</div>}

<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 0 4px",marginTop:8,borderTop:`2px solid ${t.bd}`}}>
<span style={{fontSize:18,fontWeight:700}}>NET INCOME</span>
<span style={{fontFamily:mn,fontSize:28,fontWeight:700,color:d.netIncome>=0?t.gn:t.ac}}>{d.netIncome>=0?"$":"-$"}{fmt$(Math.abs(d.netIncome))}</span>
</div>
<div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:t.td,paddingBottom:8}}>
<span>{d.daysInRange} days{d.isOpenPeriod&&<span style={{color:t.yl,fontStyle:'italic',marginLeft:6}}>estimated</span>}</span>
<span>Margin: {pct(d.netIncome)}</span>
</div>
</div>
</div>);
}
